import * as React from 'react'
import Head from 'next/head'

import {
  formatDistanceToNow,
  getContentRoutePathname,
  i18n,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { ArticlePreviewProps, Pagination } from '@thg-commerce/gravity-elements'
import { spacing, styled } from '@thg-commerce/enterprise-theme'

import {
  Category as CategoryType,
  Post as PostType,
  Tag as TagType,
  PostAuthor as PostAuthorType,
} from '../../types'
import { categoryPostValidator } from '../../validator'
import { Category } from '../Category'
import { POSTS_PER_PAGE } from '../../../pages/blog/[...slug]'

export interface CategoryPage {
  isAmp: boolean
  category: CategoryType | TagType | PostAuthorType
  page: number
  hasWebP: boolean
}

const StyledPagination = styled(Pagination)`
  margin-bottom: ${spacing(4)};
`

export const CategoryPage = (props: CategoryPage) => {
  const { contentRoutePrefix, imageServiceURL, domain } = useSiteDefinition()
  const canonical = `https://${domain}/${contentRoutePrefix}${props.category.uri}`

  const paginationi18nText = {
    next: i18n('general.pagination.next'),
    previous: i18n('general.pagination.previous'),
    ofTotal: i18n('general.pagination.oftotal'),
    page: i18n('general.pagination.page'),
    dropdownLabel: i18n('general.pagination.dropdownlabel'),
    currentlyOnText: i18n('general.pagination.currentlyontext'),
    ariaLabel: i18n('general.pagination.arialabel'),
  }

  const categoryPosts: ArticlePreviewProps[] | null | undefined =
    props.category.childPosts &&
    props.category.childPosts
      .filter((post) => categoryPostValidator.isValidSync(post))
      .map((rawPost) => {
        const post = rawPost as PostType
        const category = post?.postCategories && post?.postCategories[0]
        const postDate = new Date(post.date)
        const postDateStamp = formatDistanceToNow(postDate, {
          addSuffix: true,
        })
        let postImage = post.featuredImage?.mediaItemUrl || ''

        if (postImage !== '') {
          const fullImagePath = postImage.replace(
            /^.+blogscdn.thehut.net\/(?=app|wp-content)/g,
            '',
          )
          const imageType = props.hasWebP ? 'webp' : 'original'
          postImage = `${imageServiceURL}/xsmall/${imageType}/${fullImagePath}`
        }

        return {
          title: post.titleDecoded,
          i18nAuthorText: i18n(
            'content.authorprefix.text',
            post.postAuthorData.name,
          ),
          category: category?.name || '',

          description: post.excerpt || undefined,
          articleAge: postDateStamp,
          image: {
            isAmp: props.isAmp,
            src: [{ url: postImage }],
            alt: post.titleDecoded,
            height: '100%',
            width: '100%',
            crossOrigin: 'anonymous',
          },
          articleUrl: getContentRoutePathname(contentRoutePrefix, post.uri),
          categoryUrl: getContentRoutePathname(
            contentRoutePrefix,
            category?.uri || '',
          ),
          authorUrl: getContentRoutePathname(
            contentRoutePrefix,
            post.postAuthorData.slug,
          ),
        }
      })

  return (
    <React.Fragment>
      <Head>
        <title>{props.category.seo?.title || props.category.name}</title>
        {props.category.seo?.__typename === 'TaxonomySEO'
          ? props.category.seo?.structuredDataJsonObject && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: props.category.seo.structuredDataJsonObject,
                }}
              />
            )
          : props.category.__typename === 'User' &&
            props.category?.structuredDataJsonObject && (
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: props.category?.structuredDataJsonObject,
                }}
              />
            )}
        {props.category.seo?.metaDesc && (
          <meta name="description" content={props.category.seo?.metaDesc} />
        )}
        <link rel="canonical" href={`${canonical}`} />
      </Head>
      <Category
        hasWebP={props.hasWebP}
        category={props.category}
        categoryPosts={categoryPosts}
      />
      {props.category.count && props.category.count > POSTS_PER_PAGE && (
        <StyledPagination
          activePage={props.page}
          totalItems={props.category.count}
          itemsPerPage={POSTS_PER_PAGE}
          urlPrefix={getContentRoutePathname(
            contentRoutePrefix,
            `/${props.category.uri}page`,
          )}
          onPageChange={(page: number) => {
            window.location.assign(
              getContentRoutePathname(
                contentRoutePrefix,
                `/${props.category.uri}page/${page}/`,
              ),
            )
          }}
          align="center"
          i18nText={paginationi18nText}
          trailingSlash={true}
        />
      )}
    </React.Fragment>
  )
}
